import { useMemo, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/utils/i18n";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ConfirmModal from "@/components/ui/confirm-modal";
import type {
  EducationDetailDto,
  EnrollmentDto,
  ObjectiveDto,
} from "../../../types/client-education-detail.types";
import {
  ObjectiveCompletionStatus,
  ObjectiveType,
} from "../../../types/client-education-detail.types";
import { clientEducationDetailApi } from "../../../services/client-education-detail.api";
import {
  resolveFollowUpAction,
  type EducationFollowUpAction,
} from "../../../utils/follow-up-url";
import { resolveEducationDisplayDates } from "../../../utils/education-dates";
import { formatDateRangeLong } from "@/utils/date";
import { ChapterAccordion } from "./ChapterAccordion";
import { ObjectiveRow } from "./ObjectiveRow";
import { InlinePlayer } from "./InlinePlayer";
import sectionStyles from "../sections.module.scss";
import styles from "./ContentSection.module.scss";

interface Props {
  education: EducationDetailDto;
  enrollment: EnrollmentDto;
  autoSelectTrigger: number; // bump to auto-select last-in-progress
  onObjectiveUpdated: (updated: ObjectiveDto) => void;
  onEnrollmentProgressUpdated?: (progressPercent: number) => void;
  onRequestRefresh?: () => void | Promise<void>;
}

function flattenObjectives(education: EducationDetailDto): ObjectiveDto[] {
  const out: ObjectiveDto[] = [];
  for (const c of education.chapters ?? []) {
    for (const o of c.objectives ?? []) out.push(o);
  }
  return out;
}

function isVideoObjective(objective: ObjectiveDto): boolean {
  return (
    objective.type === ObjectiveType.INTERNAL_FILE &&
    (objective.file?.mimeType?.startsWith("video") ?? false)
  );
}

function findResumeTarget(flat: ObjectiveDto[]): ObjectiveDto | null {
  const inProgress = flat.find(
    (o) =>
      (o.completionStatus ?? o.status) ===
        ObjectiveCompletionStatus.IN_PROGRESS && !o.isLocked,
  );
  if (inProgress) return inProgress;
  const notStarted = flat.find(
    (o) =>
      (o.completionStatus ?? o.status) ===
        ObjectiveCompletionStatus.NOT_STARTED && !o.isLocked,
  );
  return notStarted ?? flat[0] ?? null;
}

export function ContentSection(props: Props) {
  const { t } = useTranslation();
  const {
    education,
    enrollment,
    autoSelectTrigger,
    onObjectiveUpdated,
    onEnrollmentProgressUpdated,
    onRequestRefresh,
  } = props;
  const chapters = education.chapters ?? [];
  const flat = useMemo(() => flattenObjectives(education), [education]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const lastSaveRef = useRef<Record<string, number>>({});
  // "Soruları cevaplayın" bilgisini her objective için bir kez göster.
  const questionsPendingNotifiedRef = useRef<Set<string>>(new Set());
  const PROGRESS_POST_INTERVAL_MS = 10_000;

  useEffect(() => {
    setActiveId((prev) => prev ?? findResumeTarget(flat)?.id ?? null);
  }, [flat]);

  useEffect(() => {
    if (autoSelectTrigger > 0) {
      const target = findResumeTarget(flat);
      if (target) setActiveId(target.id);
    }
  }, [autoSelectTrigger, flat]);

  const activeIndex = flat.findIndex((o) => o.id === activeId);
  const activeObjective = activeIndex >= 0 ? flat[activeIndex] : null;

  const navigate = useNavigate();
  const [followUp, setFollowUp] = useState<EducationFollowUpAction | null>(null);

  async function markComplete(
    obj: ObjectiveDto,
    opts?: { watchedSeconds?: number; lastPositionSeconds?: number }
  ) {
    try {
      const followUpAction = await clientEducationDetailApi.completeObjective(
        enrollment.id,
        obj.id,
        opts,
      );
      onObjectiveUpdated({
        ...obj,
        completionStatus: ObjectiveCompletionStatus.COMPLETED,
        status: ObjectiveCompletionStatus.COMPLETED,
      });
      toast.success(i18n.t("contentSection.completedSuccess"));
      // Kenar çubuğundaki ilerleme yüzdesi kayıt (enrollment) üzerinden gelir ve
      // yalnızca video ilerlemesi kaydedilirken tazeleniyordu; "Tamamlandı
      // işaretle" ile ilerleyen kullanıcıda "1/3 tamamlandı" yazarken yüzde %0
      // kalıyordu. Tamamlamadan sonra da tazeliyoruz.
      try {
        const progress = await clientEducationDetailApi.getEnrollmentProgress(
          enrollment.id,
        );
        onEnrollmentProgressUpdated?.(progress.progressPercent);
      } catch {
        // Yüzde tazelenemezse hedef yine tamamlandı; sessiz geç.
      }
      if (followUpAction) {
        // Son objective + sınav/anket aşaması varsa: popup göster
        setFollowUp(followUpAction);
      }
    } catch {
      toast.error(i18n.t("contentSection.completedError"));
    }
  }

  function handleFollowUpConfirm() {
    const snapshot = followUp;
    setFollowUp(null);
    const resolved = resolveFollowUpAction(snapshot?.actionUrl);
    if (!resolved) return;
    const mergedState =
      resolved.path === "/dashboard/client-polls"
        ? { ...(resolved.state ?? {}), pollTitle: snapshot?.title }
        : resolved.state;
    navigate(resolved.path, mergedState ? { state: mergedState } : undefined);
  }

  function handleFollowUpClose() {
    setFollowUp(null);
  }

  async function saveProgress(
    obj: ObjectiveDto,
    s: {
      watchedSeconds: number;
      lastPositionSeconds: number;
      progressPercent: number;
      watchedSecondsDelta?: number;
      segmentStartSeconds?: number;
      durationSeconds?: number;
    },
    opts?: { force?: boolean }
  ): Promise<boolean> {
    const last = lastSaveRef.current[obj.id] ?? 0;
    const now = Date.now();
    if (!opts?.force && now - last < PROGRESS_POST_INTERVAL_MS) return false;
    const delta = Math.min(Math.max(Math.floor(s.watchedSecondsDelta ?? 0), 0), 12);
    lastSaveRef.current[obj.id] = now;
    if (delta <= 0) return false;
    try {
      const watch = await clientEducationDetailApi.updateObjectiveProgress(
        enrollment.id,
        obj.id,
        {
          ...s,
          watchedSecondsDelta: delta,
        },
      );
      onObjectiveUpdated({
        ...obj,
        watchedSeconds: watch.watchedSeconds,
        lastPositionSeconds: watch.lastPositionSeconds,
        currentSecond: watch.currentSecond ?? watch.lastPositionSeconds,
        progressPercent: watch.progressPercent,
        completionStatus: watch.isCompleted
          ? ObjectiveCompletionStatus.COMPLETED
          : ObjectiveCompletionStatus.IN_PROGRESS,
        status: watch.isCompleted
          ? ObjectiveCompletionStatus.COMPLETED
          : ObjectiveCompletionStatus.IN_PROGRESS,
      });
      const progress = await clientEducationDetailApi.getEnrollmentProgress(
        enrollment.id,
      );
      onEnrollmentProgressUpdated?.(progress.progressPercent);
      if (watch.isCompleted) {
        await onRequestRefresh?.();
      } else if (watch.videoQuestionsPending && !questionsPendingNotifiedRef.current.has(obj.id)) {
        // Video izlendi ama video içi soruların hepsi doğru cevaplanmadı: backend
        // objective'i tamamlamaz (pentest 3.3.1). Kursiyeri bir kez bilgilendir.
        questionsPendingNotifiedRef.current.add(obj.id);
        toast.info(
          i18n.t("inlinePlayer.videoQuestionsPending", {
            defaultValue:
              "Videoyu tamamlamak için video içindeki soruların hepsini doğru cevaplamalısınız.",
          }),
        );
      }
      return true;
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      if (message === "This video cannot be started before the previous video is completed") {
        toast.error(i18n.t("inlinePlayer.lockedPreviousRequired"));
      }
      return false;
    }
  }

  const isSingle = chapters.length === 1;
  const headerTotalMin = chapters.reduce(
    (acc, c) => acc + ((c.durationHours ?? 0) * 60 + (c.durationMinutes ?? 0)),
    0
  );
  const doneCount = flat.filter(
    (o) => (o.completionStatus ?? o.status) === ObjectiveCompletionStatus.COMPLETED
  ).length;

  // Eğitim başlamadıysa içerik (video) erişimi kapalı. Backend de aynı
  // kontrolü uyguluyor (EDUCATION_NOT_YET_STARTED).
  const startMs = education.startDate ? new Date(education.startDate).getTime() : null;
  const notStartedYet = startMs != null && !Number.isNaN(startMs) && startMs > Date.now();
  if (notStartedYet) {
    // Başlangıç tarihinin gösterildiği her yerde bitiş tarihi de gösterilsin.
    // Eğitimin kendi bitiş tarihi yoksa sınıf/oturum tarihlerinden türetilir.
    const { start: displayStart, end: displayEnd } = resolveEducationDisplayDates(education);
    const range = formatDateRangeLong(
      displayStart ?? (education.startDate as string),
      displayEnd,
    );
    const heading = `Eğitim ${range} tarihinde başlayacak.`;
    return (
      <section id="education-content" className={sectionStyles.section}>
        <div className={sectionStyles.header}>
          <h2>{t("clientContentSection.title")}</h2>
        </div>
        <div
          style={{
            padding: "24px",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            borderRadius: 10,
            textAlign: "center",
            color: "#475569",
          }}
        >
          <strong>{heading}</strong>
          <div style={{ marginTop: 6, fontSize: 13 }}>
            Başlangıç tarihine kadar video izleme, sınav ve anket kapalıdır.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="education-content" className={sectionStyles.section}>
      <div className={sectionStyles.header}>
        <h2>{t("clientContentSection.title")}</h2>
        <span>
          {doneCount} / {flat.length} tamamlandı · toplam ~{headerTotalMin} dk
        </span>
      </div>

      <div
        className={
          chapters.length > 1 || flat.length > 1
            ? styles.layout
            : styles.layoutSolo
        }
      >
        {(chapters.length > 1 || flat.length > 1) && (
          <aside className={styles.nav}>
            {isSingle ? (
              <div className={styles.objList}>
                {flat.map((o) => (
                  <ObjectiveRow
                    key={o.id}
                    objective={o}
                    active={o.id === activeId}
                    onSelect={() => {
                      if (isVideoObjective(o) && o.isLocked) {
                        toast.warning(i18n.t("inlinePlayer.lockedPreviousRequired"));
                        return;
                      }
                      setActiveId(o.id);
                    }}
                  />
                ))}
              </div>
            ) : (
              <ChapterAccordion
                chapters={chapters}
                activeObjectiveId={activeId}
                onSelectObjective={(o) => {
                  if (isVideoObjective(o) && o.isLocked) {
                    toast.warning(i18n.t("inlinePlayer.lockedPreviousRequired"));
                    return;
                  }
                  setActiveId(o.id);
                }}
              />
            )}
          </aside>
        )}

        <div className={styles.playerPane}>
          <InlinePlayer
            objective={activeObjective}
            chapter={
              activeObjective
                ? chapters.find((c) =>
                    c.objectives?.some((o) => o.id === activeObjective.id),
                  ) ?? null
                : null
            }
            enrollmentId={enrollment.id}
            virtualClassrooms={education.virtualClassrooms}
            onMarkComplete={markComplete}
            onProgress={saveProgress}
            onRequestRefresh={onRequestRefresh}
            onPrev={
              activeIndex > 0 ? () => setActiveId(flat[activeIndex - 1].id) : undefined
            }
            onNext={
              activeIndex >= 0 &&
              activeIndex < flat.length - 1 &&
              !(isVideoObjective(flat[activeIndex + 1]) && flat[activeIndex + 1].isLocked)
                ? () => setActiveId(flat[activeIndex + 1].id)
                : undefined
            }
          />
        </div>
      </div>

      <ConfirmModal
        isOpen={!!followUp}
        onClose={handleFollowUpClose}
        onConfirm={handleFollowUpConfirm}
        title={followUp?.title ?? ""}
        message={followUp?.message ?? ""}
        confirmText={followUp?.buttonLabel ?? "Devam Et"}
        cancelText="Daha sonra"
      />
    </section>
  );
}
